import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { useAppSelector } from "@/store/hooks";
import type { GrammarSlideData } from "@ai-teach/types";
import { AiFillPicture } from "react-icons/ai";
import { BiChevronLeft } from "react-icons/bi";
import { HiVolumeUp } from "react-icons/hi";
import { IoMdArrowBack } from "react-icons/io";
import { IoMic } from "react-icons/io5";

export function GrammarSentenceLiveMobileView() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  if (!slide) return null;

  const { text, image } = slide.slideData as GrammarSlideData;

  return (
    <LiveMobileViewContainer>
      {/* Decorative background blobs */}
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-orange-200/40 rounded-full pointer-events-none" />
      <div className="absolute top-24 -left-10 w-24 h-24 bg-yellow-200/40 rounded-full pointer-events-none" />

      {/* Top Bar - Progress */}
      <div className="flex items-center gap-3 w-full z-10 shrink-0">
        <BiChevronLeft className="text-gray-600 flex-shrink-0" fontSize={28} />
        <div className="relative bg-white flex-1 h-2.5 rounded-full overflow-hidden shadow-inner">
          <div className="absolute left-0 h-full w-[60%] bg-gradient-to-r from-orange-400 to-orange-500 rounded-full" />
        </div>
        <span className="text-xs font-bold text-orange-500 shrink-0">3/5</span>
      </div>

      {/* Image Card */}
      <div className="w-full aspect-square rounded-3xl overflow-hidden border-4 border-orange-400 bg-white shadow-md shrink-0">
        {image?.src ? (
          <img
            src={image.src}
            alt={image.filename ?? "slide image"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-orange-50">
            <AiFillPicture className="text-orange-200 text-4xl" />
            <p className="text-xs text-orange-300 font-medium">No image</p>
          </div>
        )}
      </div>

      {/* Sentence Card — speech bubble style with play button */}
      <div className="w-full bg-white rounded-3xl shadow-md p-4 flex flex-col gap-3 shrink-0 relative">
        {/* "Say this!" label */}
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shrink-0">
            <span className="text-white text-[8px] font-black">!</span>
          </div>
          <span className="text-[11px] font-bold text-orange-500 uppercase tracking-widest">
            Question
          </span>
        </div>

        {/* Sentence text + play button */}
        <div className="flex items-center gap-3">
          {/* Play audio button */}
          <button
            type="button"
            className="shrink-0 w-11 h-11 bg-orange-500 hover:bg-orange-600 active:scale-95 rounded-full flex items-center justify-center shadow-md transition-all"
            style={{ boxShadow: "0 4px 0 0 #c2410c" }}
          >
            <HiVolumeUp className="text-white text-xl" />
          </button>

          {/* Sentence text */}
          <div className="flex-1">
            <p className="text-gray-800 font-semibold text-base leading-snug">
              {text || <span className="text-gray-300 italic font-normal">No sentence yet…</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom control panel */}
      <div
        className="w-full flex flex-col gap-3 bg-orange-100 absolute bottom-0 left-0 px-4 py-4 rounded-t-3xl"
        style={{ boxShadow: "0 -6px 20px -4px rgba(251, 146, 60, 0.25)" }}
      >
        {/* "Your turn!" label + record button */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs font-bold text-orange-500 uppercase tracking-widest">
            Your turn! 🎤
          </span>

          {/* Record button with glow rings */}
          <div className="relative flex items-center justify-center">
            {/* Outer glow ring */}
            <div className="absolute w-20 h-20 bg-orange-300/30 rounded-full animate-ping" />
            {/* Mid ring */}
            <div className="absolute w-16 h-16 bg-orange-300/40 rounded-full" />
            {/* Main button */}
            <button
              type="button"
              className="relative z-10 w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95"
              style={{ boxShadow: "0 5px 0 0 #c2410c" }}
            >
              {/* Animated waveform bars */}
              <div className="flex items-end justify-center gap-0.5 h-6">
                <div className="bg-white w-1 h-2 rounded-sm animate-pulse" />
                <div
                  className="bg-white w-1 h-4 rounded-sm animate-pulse"
                  style={{ animationDelay: "0.1s" }}
                />
                <div
                  className="bg-white w-1 h-5 rounded-sm animate-pulse"
                  style={{ animationDelay: "0.2s" }}
                />
                <div
                  className="bg-white w-1 h-3 rounded-sm animate-pulse"
                  style={{ animationDelay: "0.3s" }}
                />
                <div
                  className="bg-white w-1 h-4 rounded-sm animate-pulse"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex w-full gap-3">
          <div
            className="w-14 h-12 bg-white rounded-2xl flex items-center justify-center"
            style={{ boxShadow: "0 4px 0 0 #d1d5db" }}
          >
            <IoMdArrowBack fontSize={22} className="text-gray-700" />
          </div>
          <div
            className="flex-1 h-12 bg-orange-500 rounded-2xl flex items-center justify-center"
            style={{ boxShadow: "0 4px 0 0 #c2410c" }}
          >
            <span className="text-white font-black text-sm tracking-widest">NEXT</span>
          </div>
        </div>
      </div>
    </LiveMobileViewContainer>
  );
}
